import React from 'react';
import { DollarSign, BarChart, Building2, Clock } from 'lucide-react';

/**
 * Компонент секции основных параметров для калькулятора рентабельности
 */
const BasicParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  industryTypes,
  companySizes,
  analysisPeriods
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Выручка */}
      <div>
        <label htmlFor="revenue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
          Выручка (руб.)
        </label>
        <input
          type="text"
          id="revenue"
          name="revenue"
          value={formData.revenue}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.revenue ? 'input-error' : ''}`}
          placeholder="Введите сумму выручки"
        />
        {errors.revenue && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.revenue}
          </p>
        )}
      </div>

      {/* Себестоимость */}
      <div>
        <label htmlFor="costOfGoods" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
          Себестоимость (руб.)
        </label>
        <input
          type="text"
          id="costOfGoods"
          name="costOfGoods"
          value={formData.costOfGoods}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.costOfGoods ? 'input-error' : ''}`}
          placeholder="Введите себестоимость"
        />
        {errors.costOfGoods && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.costOfGoods}
          </p>
        )}
      </div>

      {/* Операционные расходы */}
      <div>
        <label htmlFor="operatingExpenses" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
          Операционные расходы (руб.)
        </label>
        <input
          type="text"
          id="operatingExpenses"
          name="operatingExpenses"
          value={formData.operatingExpenses}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.operatingExpenses ? 'input-error' : ''}`}
          placeholder="Введите операционные расходы"
        />
        {errors.operatingExpenses && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.operatingExpenses}
          </p>
        )}
      </div>

      {/* Сумма инвестиций/активов */}
      <div>
        <label htmlFor="investmentAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
          Сумма инвестиций/активов (руб.)
        </label>
        <input
          type="text"
          id="investmentAmount"
          name="investmentAmount"
          value={formData.investmentAmount}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.investmentAmount ? 'input-error' : ''}`}
          placeholder="Введите сумму инвестиций"
        />
        {errors.investmentAmount && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.investmentAmount}
          </p>
        )}
      </div>

      {/* Ставка налога на прибыль */}
      <div>
        <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <BarChart className="w-4 h-4 mr-2 text-emerald-500" />
          Ставка налога на прибыль (%)
        </label>
        <input
          type="text"
          id="taxRate"
          name="taxRate"
          value={formData.taxRate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.taxRate ? 'input-error' : ''}`}
          placeholder="Например: 20"
        />
        {errors.taxRate && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.taxRate}
          </p>
        )}
      </div>

      {/* Тип отрасли */}
      <div>
        <label htmlFor="industryType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Building2 className="w-4 h-4 mr-2 text-emerald-500" />
          Отрасль бизнеса
        </label>
        <select
          id="industryType"
          name="industryType"
          value={formData.industryType}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {industryTypes.map((industry) => (
            <option key={industry.id} value={industry.id}>
              {industry.name}
            </option>
          ))}
        </select>
        {errors.industryType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.industryType}
          </p>
        )}
      </div>

      {/* Размер компании */}
      <div>
        <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Building2 className="w-4 h-4 mr-2 text-emerald-500" />
          Размер бизнеса
        </label>
        <select
          id="companySize"
          name="companySize"
          value={formData.companySize}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {companySizes.map((size) => (
            <option key={size.id} value={size.id}>
              {size.name}
            </option>
          ))}
        </select>
        {errors.companySize && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.companySize}
          </p>
        )}
      </div>

      {/* Период анализа */}
      <div>
        <label htmlFor="period" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Clock className="w-4 h-4 mr-2 text-emerald-500" />
          Период анализа
        </label>
        <select
          id="period"
          name="period"
          value={formData.period}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {analysisPeriods.map((period) => (
            <option key={period.id} value={period.id}>
              {period.name}
            </option>
          ))}
        </select>
        {errors.period && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.period}
          </p>
        )}
      </div>

      {/* Информационная подсказка */}
      <div className="col-span-full mt-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Все указанные финансовые показатели должны относиться к одному периоду ({formData.period === 'year' ? 'году' : formData.period === 'quarter' ? 'кварталу' : formData.period === 'half_year' ? 'полугодию' : 'месяцу'}). Для корректного расчета используйте данные из бухгалтерской отчетности.
        </p>
      </div>
    </div>
  );
};

export default BasicParametersSection;